import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { isAdmin } from "../../../redux/config";
import InnerLayout from "../../../components/layouts/inner-layout";
import { DataContext } from "../../../components/layouts/main-layout";
import successToast from "../../../components/helper-components/success-toast";
import errorToast from "../../../components/helper-components/error-toast";
import {
  loyaltyConfigGetAction,
  loyaltyConfigUpdateAction,
} from "../../../redux/actions/loyalty-action";
import SubTabs from "./components/sub-tabs";
import { FormBar } from "./components/primitives";
import OverviewTab from "./tabs/overview-tab";
import EarningRulesTab from "./tabs/earning-rules-tab";
import RedemptionTab from "./tabs/redemption-tab";
import TiersTab from "./tabs/tiers-tab";

const HASH_TAB = (h) => {
  const v = (h || "").replace("#", "");
  return ["overview", "earn", "redeem", "tiers"].includes(v) ? v : "overview";
};

const LoyaltyAdmin = ({ embedded = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { permissionFn } = useContext(DataContext);

  const [tab, setTab] = useState(() => HASH_TAB(window.location.hash));
  const [original, setOriginal] = useState(null);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  // Read-only on POS instances. `isAdmin` is the existing build-time flag
  // (true on CPOS, false on POS) — reused here instead of a separate cpos flag.
  const readOnly = !isAdmin;

  // permission gate using existing flow
  const perm = permissionFn ? permissionFn("loyalty_admin") || {} : { all: true };

  useEffect(() => {
    dispatch(
      loyaltyConfigGetAction(
        (res) => {
          const cfg = res?.data || {};
          setOriginal(JSON.parse(JSON.stringify(cfg)));
          setConfig(JSON.parse(JSON.stringify(cfg)));
        },
        () => errorToast("Failed to load loyalty config")
      )
    );
  }, [dispatch]);

  useEffect(() => {
    const onHash = () => setTab(HASH_TAB(window.location.hash));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const goTo = (t) => {
    setTab(t);
    window.location.hash = t;
    document.querySelector(".loyalty-scroller")?.scrollTo(0, 0);
  };

  const isDirty = original && JSON.stringify(original) !== JSON.stringify(config);

  const handleSave = () => {
    if (!config) return;
    setSaving(true);
    // Backend Joi schema is closed — only send the fields it accepts.
    // Tier roster + category multipliers nest INSIDE earn_config /
    // redeem_config (post senior-review reshape). TiersTab edits the joined
    // shape and writes back into both buckets; we send those buckets
    // verbatim here. Top-level `tiers`/`category_multipliers` keys would be
    // stripped by Joi `stripUnknown:true` and silently dropped.
    const payload = {
      earn_config: config.earn_config,
      redeem_config: config.redeem_config,
    };
    dispatch(
      loyaltyConfigUpdateAction(
        payload,
        (res) => {
          setSaving(false);
          const next = res?.data || config;
          setOriginal(JSON.parse(JSON.stringify(next)));
          setConfig(JSON.parse(JSON.stringify(next)));
          successToast("Loyalty config saved");
        },
        (err) => {
          setSaving(false);
          errorToast(err?.message || "Save failed");
        }
      )
    );
  };

  const handleCancel = () => {
    if (!original) return;
    if (isDirty && !window.confirm("Discard unsaved changes?")) return;
    setConfig(JSON.parse(JSON.stringify(original)));
  };

  if (perm.all === false) {
    const denied = (
      <div className="h-full flex items-center justify-center text-[#64748B]">
        You don't have permission to view Loyalty Admin.
      </div>
    );
    return embedded ? denied : <InnerLayout title="Settings">{denied}</InnerLayout>;
  }

  const body = (
    <div className={`flex flex-col ${embedded ? "" : "h-full bg-[#FAFBFC]"}`}>
      {!embedded && (
        <div className="flex items-center gap-2 px-6 py-3 text-[13px] text-[#475467]">
          <button
            className="flex items-center gap-1 hover:text-[#0F172A]"
            onClick={() => navigate("/settings")}
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Discount Settings
          </button>
          <span>/</span>
          <span className="text-[#0F172A] font-medium">Loyalty</span>
          {readOnly && (
            <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#FFF3D6] text-[#9A6700]">
              Read-only at POS — edit in CPOS
            </span>
          )}
        </div>
      )}

      <SubTabs active={tab} onChange={goTo} />

        <div className="loyalty-scroller flex-1 overflow-y-auto">
          <div className="px-6 py-5 max-w-[1400px]">
            {!config ? (
              <div className="text-[13px] text-[#64748B]">Loading…</div>
            ) : tab === "overview" ? (
              <OverviewTab config={config} goTo={goTo} />
            ) : tab === "earn" ? (
              <EarningRulesTab config={config} setConfig={setConfig} readOnly={readOnly} />
            ) : tab === "redeem" ? (
              <RedemptionTab config={config} setConfig={setConfig} readOnly={readOnly} />
            ) : tab === "tiers" ? (
              <TiersTab config={config} setConfig={setConfig} readOnly={readOnly} />
            ) : null}

            {!readOnly && tab !== "overview" && isDirty && (
              <FormBar
                hint="Changes apply to new transactions only."
                onCancel={handleCancel}
                onSave={handleSave}
                saving={saving}
              />
            )}
          </div>
        </div>
    </div>
  );

  return embedded ? body : <InnerLayout title="Settings">{body}</InnerLayout>;
};

export default LoyaltyAdmin;
